// PayoutList.js
// Team-level Payout page (each team = a CPO). Pick a year and month, choose which
// chargers to include (all by default), generate the payout, and download it as a
// styled Excel or branded PDF. Team totals + per-charger breakdown.
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import ScheduleIcon from "@mui/icons-material/Schedule";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import Page from "src/components/Page";
import Token from "src/util/Token";
import {
  getTeamPayoutReport,
  finalizePayoutUrl,
  overridePayoutUrl,
  emailPayoutUrl,
  payoutEmailSchedulesUrl,
  payoutEmailScheduleUrl,
} from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useList } from "src/hooks/useFetch.hook";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import { isAmikPayoutDomain } from "src/components/charger/charger.constants";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import {
  S,
  withComma,
  fmtTime,
  buildChargingSummary,
  buildPayoutSummaryRows,
  buildPaymentTypeSections,
  sumOverDelivered,
  loadLogoDataUrl,
  formatPayoutRule,
} from "./payoutShared";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Transaction detail columns, ordered for CPO visibility: identity -> timing ->
// energy -> money.
const TXN_COLUMNS = [
  { key: "chargerId", label: "Charger" },
  { key: "connectorId", label: "Connector" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "paymentId", label: "Payment ID" },
  { key: "paymentType", label: "Type" },
  { key: "startTime", label: "Start Time", time: true },
  { key: "stopTime", label: "Stop Time", time: true },
  { key: "kWh", label: "kWh Consumed", num: true },
  { key: "pricePerKw", label: "Price/kWh (incl. GST)", money: true },
  { key: "chargingCost", label: "Charging Cost (incl. GST)", money: true },
  { key: "taxAmount", label: "GST", money: true },
  { key: "pgCharge", label: "Payment Gateway Fee", money: true },
  { key: "amikFee", label: "Amik CMS Fee (incl. GST)", money: true },
  { key: "payout", label: "Payout (incl. GST)", money: true },
  {
    key: "overDeliveredAmount",
    label: "Over-delivery (Adj.)",
    money: true,
    blankZero: true,
  },
];

const TXN_SCREEN_LIMIT = 500;

export default function PayoutList() {
  const { teamId, team } = useSelectedTeam();
  const contextTeamId =
    teamId && typeof teamId === "object" ? teamId.id : teamId;
  const domainConfig = useDomainConfig();
  const brand = domainConfig?.name || "Amik";
  const isGrandParent = useIsGrandParent();
  const isRootSuperAdmin = useIsRootSuperAdmin();
  const payoutViewAccess = useAccess(FEATURE.PAYOUT, "V");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Same rule the sidebar applies, so the tab and the route agree: the ROOT
  // super admin is unlimited, a company-level user on an Amik-branded host keeps
  // the access they have today, and a limited super admin keeps Payout unless
  // their role explicitly denies it — a Payout row set to "N". No row at all
  // means the feature does not exist to be granted yet, which must not read as
  // a denial. Without this the hidden tab would still be one URL away.
  const payoutAccessRow = (team?.access ?? []).find(
    (item) => item.featureName === FEATURE.PAYOUT
  );
  const payoutExplicitlyDenied =
    !!payoutAccessRow && payoutAccessRow.view !== "Y";
  const canViewPayout =
    isRootSuperAdmin ||
    payoutViewAccess ||
    (isAmikPayoutDomain() && !isGrandParent) ||
    (isGrandParent && !payoutExplicitlyDenied);

  useEffect(() => {
    if (!canViewPayout) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized" })
      );
      navigate("/dashboard");
    }
  }, [canViewPayout, dispatch, navigate]);

  // GP (super admin) has access to every company, so the Payout screen lets them
  // drill Company → Team → chargers instead of using the top-right team selector.
  // Everyone else keeps using the selected team from context.
  const [gpCompany, setGpCompany] = useState("");
  const [gpTeam, setGpTeam] = useState("");
  const { data: companies = [] } = useList("companyList", isGrandParent);
  const { data: gpTeams = [] } = useList(
    "teams",
    isGrandParent && !!gpCompany,
    gpCompany
  );
  const resolvedTeamId = isGrandParent ? gpTeam : contextTeamId;

  const { data: chargers = [], isFetching: chargersLoading } = useList(
    "chargers",
    isGrandParent ? !!gpTeam : undefined,
    isGrandParent ? gpTeam : undefined
  );

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  // Default selection: last (previous completed) month.
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const defaultYear = lastMonthDate.getFullYear();
  const defaultMonth = lastMonthDate.getMonth() + 1;

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [selectedChargers, setSelectedChargers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("summary");

  // Default all chargers selected, re-defaulting whenever the selected team changes.
  // Keyed on the team we last initialised for (not a run-once flag), so switching
  // teams from the top-right dropdown refreshes the charger list. Guarded by
  // !chargersLoading so we seed from the new team's chargers, not the previous
  // team's stale list; the ref guard keeps a user's manual de-selection intact
  // across unrelated re-renders (chargers is a fresh array ref each render).
  const initedTeamRef = useRef(null);
  useEffect(() => {
    if (
      !chargersLoading &&
      chargers.length &&
      initedTeamRef.current !== resolvedTeamId
    ) {
      initedTeamRef.current = resolvedTeamId;
      setSelectedChargers(chargers.map((c) => c.chargepoint_pk));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargers, chargersLoading, resolvedTeamId]);

  // On team switch, immediately drop the previous team's selection and any report
  // it generated so nothing stale lingers while the new charger list loads.
  const prevTeamRef = useRef(resolvedTeamId);
  useEffect(() => {
    if (prevTeamRef.current !== resolvedTeamId) {
      prevTeamRef.current = resolvedTeamId;
      setSelectedChargers([]);
      setData(null);
      setError(null);
    }
  }, [resolvedTeamId]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);
    return years;
  }, [currentYear]);

  const monthOptions = useMemo(() => {
    const opts = MONTHS.map((label, i) => ({ value: i + 1, label }));
    // Only past/current-completed months for the current year.
    return year === currentYear ? opts.filter((m) => m.value <= currentMonth) : opts;
  }, [year, currentYear, currentMonth]);

  const periodLabel = month ? `${MONTHS[month - 1]} ${year}` : "";
  const allSelected =
    chargers.length > 0 && selectedChargers.length === chargers.length;

  const handleChargerChange = (e) => {
    const val = e.target.value;
    setSelectedChargers(typeof val === "string" ? val.split(",") : val);
    setData(null);
  };

  const handleGenerate = async () => {
    if (isGrandParent && !gpTeam) {
      setError("Please select a company and team.");
      return;
    }
    if (!month) {
      setError("Please select a month.");
      return;
    }
    if (!selectedChargers.length) {
      setError("Please select at least one charger.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setData(null);
      const token = Token.get();
      // When every charger is selected, omit the filter (backend defaults to all).
      const idsParam = allSelected ? null : selectedChargers;
      const response = await axios.get(
        getTeamPayoutReport(resolvedTeamId, year, month, idsParam),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setData(response.data.data);
        setTab("summary");
      } else {
        setError(response.data?.message || "Failed to generate payout report.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to generate payout report."
      );
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------- finalize / override (super admin) --
  const [finalizing, setFinalizing] = useState(false);
  const [editRow, setEditRow] = useState(null); // charger being overridden
  const [editPayout, setEditPayout] = useState("");
  const [editFee, setEditFee] = useState("");
  const [editNote, setEditNote] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      setError(null);
      const token = Token.get();
      await axios.post(
        finalizePayoutUrl(resolvedTeamId, year, month),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await handleGenerate(); // refetch — now served from the frozen snapshot
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to finalize payout."
      );
    } finally {
      setFinalizing(false);
    }
  };

  const openEdit = (cg) => {
    setEditRow(cg);
    setEditPayout(String(cg.payout?.payout ?? ""));
    setEditFee(String(cg.payout?.amikFee ?? ""));
    setEditNote(cg.overrideNote || "");
  };

  // Which fields the admin actually changed from the finalized values — only those
  // are sent, so an unchanged field is never submitted as an "override".
  const overrideDelta = () => {
    if (!editRow) return { payout: null, amikFee: null, changed: false };
    const op = Number(editRow.payout?.payout);
    const of = Number(editRow.payout?.amikFee);
    const np = editPayout === "" ? null : Number(editPayout);
    const nf = editFee === "" ? null : Number(editFee);
    const payoutChanged = np !== null && !Number.isNaN(np) && np !== op;
    const feeChanged = nf !== null && !Number.isNaN(nf) && nf !== of;
    return {
      payout: payoutChanged ? np : null,
      amikFee: feeChanged ? nf : null,
      changed: payoutChanged || feeChanged,
    };
  };

  const handleSaveOverride = async () => {
    if (!editRow) return;
    const delta = overrideDelta();
    if (!delta.changed) return;
    try {
      setSavingOverride(true);
      setError(null);
      const token = Token.get();
      await axios.put(
        overridePayoutUrl(resolvedTeamId, year, month, editRow.chargepointPk),
        { payout: delta.payout, amikFee: delta.amikFee, note: editNote || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditRow(null);
      await handleGenerate(); // refetch to show overridden totals
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to override payout."
      );
    } finally {
      setSavingOverride(false);
    }
  };

  // ----------------------------------------- email / schedule (super admin) --
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailPdf, setEmailPdf] = useState(true);
  const [emailExcel, setEmailExcel] = useState(true);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const [schedOpen, setSchedOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedMsg, setSchedMsg] = useState(null);
  const [defaultRecipient, setDefaultRecipient] = useState("");
  const [schedForm, setSchedForm] = useState({
    type: "MONTHLY",
    recipients: "",
    dayOfMonth: 2,
    sendAt: "",
    targetYear: defaultYear,
    targetMonth: defaultMonth,
    attachPdf: true,
    attachExcel: true,
  });
  const [schedBusy, setSchedBusy] = useState(false);

  const authGet = () => ({ headers: { Authorization: `Bearer ${Token.get()}` } });

  const fetchSchedules = async () => {
    const res = await axios.get(payoutEmailSchedulesUrl(resolvedTeamId), authGet());
    const d = res.data?.data || {};
    setSchedules(d.schedules || []);
    setDefaultRecipient(d.defaultRecipient || "");
    return d.defaultRecipient || "";
  };

  const openEmailDialog = async () => {
    setEmailResult(null);
    setEmailSubject("");
    setEmailMessage("");
    setEmailPdf(true);
    setEmailExcel(true);
    try {
      const def = await fetchSchedules();
      setEmailRecipients(def || "");
    } catch (e) {
      setEmailRecipients("");
    }
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    try {
      setEmailSending(true);
      setEmailResult(null);
      const idsParam = allSelected ? [] : selectedChargers;
      const res = await axios.post(
        emailPayoutUrl(resolvedTeamId, year, month),
        {
          recipients: emailRecipients,
          chargerIds: idsParam,
          subject: emailSubject || undefined,
          message: emailMessage || undefined,
          attachPdf: emailPdf,
          attachExcel: emailExcel,
        },
        authGet()
      );
      setEmailResult({ ok: true, msg: res.data?.message || "Emailed." });
    } catch (e) {
      setEmailResult({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to send.",
      });
    } finally {
      setEmailSending(false);
    }
  };

  const openSchedules = async () => {
    setSchedMsg(null);
    setSchedForm((f) => ({ ...f, targetYear: year, targetMonth: month }));
    try {
      const def = await fetchSchedules();
      setSchedForm((f) => ({ ...f, recipients: def || "" }));
    } catch (e) {
      /* ignore */
    }
    setSchedOpen(true);
  };

  const handleCreateSchedule = async () => {
    try {
      setSchedBusy(true);
      await axios.post(
        payoutEmailSchedulesUrl(resolvedTeamId),
        {
          type: schedForm.type,
          recipients: schedForm.recipients,
          dayOfMonth: schedForm.dayOfMonth,
          sendAt: schedForm.sendAt
            ? schedForm.sendAt.replace("T", " ") + ":00"
            : undefined,
          targetYear: schedForm.targetYear,
          targetMonth: schedForm.targetMonth,
          attachPdf: schedForm.attachPdf,
          attachExcel: schedForm.attachExcel,
        },
        authGet()
      );
      await fetchSchedules();
      setSchedForm((f) => ({ ...f, sendAt: "" }));
      setSchedMsg({ ok: true, msg: "Schedule added." });
    } catch (e) {
      setSchedMsg({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to save schedule.",
      });
    } finally {
      setSchedBusy(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await axios.delete(payoutEmailScheduleUrl(resolvedTeamId, id), authGet());
      await fetchSchedules();
      setSchedMsg({ ok: true, msg: "Schedule deleted." });
    } catch (e) {
      setSchedMsg({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to delete schedule.",
      });
    }
  };

  const handleToggleSchedule = async (row) => {
    try {
      await axios.put(
        payoutEmailScheduleUrl(resolvedTeamId, row.schedule_pk),
        { active: row.active ? false : true },
        authGet()
      );
      await fetchSchedules();
    } catch (e) {
      setSchedMsg({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to update schedule.",
      });
    }
  };

  const payoutRows = (summary, payout, meta, transactions) =>
    buildPayoutSummaryRows(
      summary,
      payout,
      meta,
      brand,
      sumOverDelivered(transactions),
      data?.byPaymentType
    );

  // One "Charging Amount Collected by Payment Type"-style card. Used to render the
  // collected types, RFID, and OCPI as three separate sections, each with its own
  // total so the settled-outside energy is never mixed into the collected figures.
  const renderPaymentTypeSection = (
    title,
    rows,
    caption,
    emptyText,
    firstColLabel = "Payment Type",
    keyId
  ) => {
    const txns = rows.reduce((a, p) => a + (Number(p.transactions) || 0), 0);
    const charging = rows.reduce(
      (a, p) => a + (Number(p.totalChargingCost) || 0),
      0
    );
    return (
      <Card key={keyId} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {caption && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            {caption}
          </Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{firstColLabel}</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell align="right">Charging Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.map((p) => (
                  <TableRow key={p.paymentType}>
                    <TableCell>{p.paymentType}</TableCell>
                    <TableCell align="right">{p.transactions}</TableCell>
                    <TableCell align="right">
                      {withComma(p.totalChargingCost)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="caption" color="text.secondary">
                      {emptyText || "No transactions."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{txns}</TableCell>
                <TableCell align="right">{withComma(charging)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  // ------------------------------------------------------------- downloads --
  const buildSummarySheet = () => {
    const {
      summary,
      payout,
      chargers: rows,
      meta,
      transactions,
      byPaymentType,
    } = data;
    const cur = meta?.currency || "INR";
    const grid = [];
    const merges = [];
    const styleMap = {};
    const ensure = (r) => {
      while (grid.length <= r) grid.push([]);
      return grid[r];
    };
    const put = (r, c, v, s) => {
      const row = ensure(r);
      while (row.length <= c) row.push("");
      row[c] = v;
      if (s) styleMap[`${r},${c}`] = s;
    };
    const band = (r, label, s, width) => {
      put(r, 0, label, s);
      for (let c = 1; c < width; c++) put(r, c, "", s);
      merges.push({ s: { r, c: 0 }, e: { r, c: width - 1 } });
    };
    const WIDTH = 8;

    let r = 0;
    band(r, "PAYOUT REPORT", S.title, WIDTH);
    r += 1;
    band(
      r,
      `Period: ${periodLabel}    |    Currency: ${cur}    |    Chargers: ${
        meta?.selectedChargers ?? rows.length
      } of ${meta?.totalChargers ?? rows.length}    |    Generated: ${new Date().toLocaleDateString(
        "en-IN"
      )}`,
      S.meta,
      WIDTH
    );
    r += 2;

    // Charging Summary (team totals).
    band(r, "CHARGING SUMMARY (ALL SELECTED CHARGERS)", S.section, WIDTH);
    r += 1;
    buildChargingSummary(summary, { includePrice: false, byPaymentType }).forEach((m) => {
      put(r, 0, m.label, S.label);
      put(
        r,
        1,
        Number(m.value) || 0,
        m.int ? S.number : m.bold ? S.moneyBold : S.money
      );
      r += 1;
    });
    r += 1;

    // Payout Summary (with/without GST).
    band(r, "PAYOUT SUMMARY", S.section, WIDTH);
    r += 1;
    put(r, 0, "Item", S.header);
    put(r, 1, "Without GST", S.header);
    put(r, 2, "With GST", S.header);
    r += 1;
    payoutRows(summary, payout, meta, transactions).forEach((pr) => {
      put(r, 0, pr.label, pr.highlight ? S.labelBold : S.label);
      put(r, 1, Number(pr.woGst) || 0, pr.highlight ? S.moneyBold : S.money);
      put(r, 2, Number(pr.wGst) || 0, pr.highlight ? S.highlight : S.moneyBold);
      r += 1;
    });
    put(r, 0, `Payout = Total Collection By Amik − ${brand} CMS Fee`, S.note);
    r += 2;

    // Per-charger breakdown.
    band(r, "PER-CHARGER PAYOUT", S.section, WIDTH);
    r += 1;
    [
      "Charger",
      "Type",
      "Transactions",
      "kWh",
      "Avg Price/kWh (incl. GST)",
      "Charging Amount Collected",
      `${brand} CMS Fee`,
      "Payout",
    ].forEach((h, c) => put(r, c, h, S.header));
    r += 1;
    rows.forEach((cg) => {
      put(r, 0, cg.chargerId, S.label);
      put(r, 1, cg.chargerType || "", S.label);
      put(r, 2, Number(cg.summary.totalTransactions) || 0, S.number);
      put(r, 3, Number(cg.summary.totalKWh) || 0, S.money);
      put(r, 4, Number(cg.summary.avgPricePerKw) || 0, S.money);
      put(r, 5, Number(cg.summary.totalChargingCost) || 0, S.money);
      put(r, 6, Number(cg.payout.amikFee) || 0, S.money);
      put(r, 7, Number(cg.payout.payout) || 0, S.moneyBold);
      r += 1;
    });
    // Total row (price omitted — it differs per charger).
    put(r, 0, "Total", S.labelBold);
    put(r, 1, "", S.labelBold);
    put(r, 2, Number(summary.totalTransactions) || 0, S.number);
    put(r, 3, Number(summary.totalKWh) || 0, S.moneyBold);
    put(r, 4, "", S.labelBold);
    put(r, 5, Number(summary.totalChargingCost) || 0, S.moneyBold);
    put(r, 6, Number(payout.amikFee) || 0, S.moneyBold);
    put(r, 7, Number(payout.payout) || 0, S.highlight);
    r += 2;

    // Charging by settlement bucket: collected (UPI/CARD/…), RFID, and OCPI
    // segregated per roaming company — one block each, matching the on-screen view.
    buildPaymentTypeSections(byPaymentType, transactions).forEach((sec) => {
      band(r, sec.title.toUpperCase(), S.section, WIDTH);
      r += 1;
      [sec.firstColLabel, "Transactions", "Charging Amount Collected"].forEach(
        (h, c) => put(r, c, h, S.header)
      );
      r += 1;
      let secTxns = 0;
      let secCharging = 0;
      if (sec.rows.length) {
        sec.rows.forEach((p) => {
          put(r, 0, p.paymentType, S.label);
          put(r, 1, Number(p.transactions) || 0, S.number);
          put(r, 2, Number(p.totalChargingCost) || 0, S.money);
          secTxns += Number(p.transactions) || 0;
          secCharging += Number(p.totalChargingCost) || 0;
          r += 1;
        });
      } else {
        put(r, 0, sec.emptyText, S.label);
        r += 1;
      }
      put(r, 0, "Total", S.labelBold);
      put(r, 1, secTxns, S.number);
      put(r, 2, secCharging, S.moneyBold);
      r += 2;
    });

    put(r, 0, `Payout rule: ${formatPayoutRule(meta)}`, S.note);
    r += 1;
    put(
      r,
      0,
      "Total Collection By Amik includes GST and covers all payment types. Price per kWh is GST-inclusive.",
      S.note
    );

    const ws = XLSX.utils.aoa_to_sheet(grid);
    Object.entries(styleMap).forEach(([key, s]) => {
      const [rr, cc] = key.split(",").map(Number);
      const addr = XLSX.utils.encode_cell({ r: rr, c: cc });
      if (!ws[addr]) ws[addr] = { t: "s", v: "" };
      ws[addr].s = s;
    });
    ws["!merges"] = merges;
    ws["!cols"] = [
      { wch: 26 },
      { wch: 8 },
      { wch: 14 },
      { wch: 12 },
      { wch: 20 },
      { wch: 24 },
      { wch: 18 },
      { wch: 16 },
    ];
    return ws;
  };

  // Transaction Details sheet. Only the header is styled (per-cell styling of
  // thousands of rows would be slow); money/energy values stay as numbers.
  const buildTransactionsSheet = () => {
    const txns = data.transactions || [];
    const header = TXN_COLUMNS.map((c) => c.label);
    const body = txns.map((t) =>
      TXN_COLUMNS.map((c) => {
        const v = t[c.key];
        if (c.time) return fmtTime(v);
        if (c.blankZero && !Number(v)) return "";
        if (c.money || c.num) return Number(v) || 0;
        if (c.key === "paymentType" && t.ocpiCompany)
          return `${t.paymentType} · ${t.ocpiCompany}`;
        return v ?? "";
      })
    );
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    header.forEach((_, c) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (sheet[addr]) sheet[addr].s = S.header;
    });
    sheet["!cols"] = TXN_COLUMNS.map((c) => ({ wch: c.time ? 20 : 15 }));
    return sheet;
  };

  const handleDownloadExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildSummarySheet(), "Payout");
    XLSX.utils.book_append_sheet(
      wb,
      buildTransactionsSheet(),
      "Transaction Details"
    );
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Payout_${periodLabel.replace(" ", "_")}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    if (!data) return;
    const {
      summary,
      payout,
      chargers: rows,
      meta,
      transactions,
      byPaymentType,
    } = data;
    const cur = meta?.currency || "INR";
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const logo = await loadLogoDataUrl();
    if (logo?.dataUrl) {
      const w = 34;
      const h = Math.max(10, (logo.height / logo.width) * w);
      try {
        doc.addImage(logo.dataUrl, "PNG", 14, 10, w, h);
      } catch (e) {
        /* logo optional */
      }
    }
    doc.setFontSize(16);
    doc.setTextColor(31, 56, 100);
    doc.text("Payout Report", pageWidth - 14, 18, { align: "right" });
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(brand, pageWidth - 14, 25, { align: "right" });

    doc.setFontSize(10);
    doc.setTextColor(40);
    const y = 40;
    doc.text(`Period: ${periodLabel}`, 14, y);
    doc.text(
      `Chargers: ${meta?.selectedChargers ?? rows.length} of ${
        meta?.totalChargers ?? rows.length
      }`,
      14,
      y + 6
    );
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 14, y + 12);
    doc.text(`Currency: ${cur}`, 14, y + 18);

    autoTable(doc, {
      startY: 64,
      head: [["Charging Summary", cur]],
      body: buildChargingSummary(summary, { includePrice: false, byPaymentType }).map((m) => [
        m.label,
        m.int ? String(m.value) : withComma(m.value),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100], halign: "left" },
      columnStyles: { 1: { halign: "right" } },
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Payout Summary", "Without GST", "With GST"]],
      body: payoutRows(summary, payout, meta, transactions).map((pr) => [
        pr.label,
        withComma(pr.woGst),
        withComma(pr.wGst),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100], halign: "left" },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right", fontStyle: "bold" },
      },
      theme: "grid",
      margin: { left: 14, right: 14 },
      didParseCell: (d) => {
        if (d.section === "body" && d.row.raw[0] === "Payout") {
          d.cell.styles.fillColor = [255, 242, 168];
          d.cell.styles.fontStyle = "bold";
        }
      },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [
        [
          "Charger",
          "Type",
          "Txns",
          "kWh",
          "Price/kWh (incl. GST)",
          "Charging Amount Collected",
          `${brand} CMS Fee`,
          "Payout",
        ],
      ],
      body: rows.map((cg) => [
        cg.chargerId,
        cg.chargerType || "",
        cg.summary.totalTransactions,
        withComma(cg.summary.totalKWh),
        withComma(cg.summary.avgPricePerKw),
        withComma(cg.summary.totalChargingCost),
        withComma(cg.payout.amikFee),
        withComma(cg.payout.payout),
      ]),
      foot: [
        [
          "Total",
          "",
          summary.totalTransactions,
          withComma(summary.totalKWh),
          "",
          withComma(summary.totalChargingCost),
          withComma(payout.amikFee),
          withComma(payout.payout),
        ],
      ],
      styles: { fontSize: 8 },
      headStyles: { fillColor: [31, 56, 100] },
      footStyles: { fillColor: [234, 240, 248], textColor: 20, fontStyle: "bold" },
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    // Charging by settlement bucket: collected, RFID, and OCPI per roaming company
    // — one table each, matching the on-screen view.
    buildPaymentTypeSections(byPaymentType, transactions).forEach((sec) => {
      const secTxns = sec.rows.reduce(
        (a, p) => a + (Number(p.transactions) || 0),
        0
      );
      const secCharging = sec.rows.reduce(
        (a, p) => a + (Number(p.totalChargingCost) || 0),
        0
      );
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [[sec.firstColLabel, "Transactions", "Charging Amount Collected"]],
        body: sec.rows.length
          ? sec.rows.map((p) => [
              p.paymentType,
              p.transactions,
              withComma(p.totalChargingCost),
            ])
          : [[sec.emptyText, "", ""]],
        foot: [["Total", secTxns, withComma(secCharging)]],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [31, 56, 100] },
        footStyles: {
          fillColor: [234, 240, 248],
          textColor: 20,
          fontStyle: "bold",
        },
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
        theme: "grid",
        margin: { left: 14, right: 14 },
      });
    });

    doc.setFontSize(8);
    doc.setTextColor(120);
    const noteY = doc.lastAutoTable.finalY + 8;
    doc.text(`Payout rule: ${formatPayoutRule(meta)}`, 14, noteY);
    doc.text(
      `Payout = Total Collection By Amik - ${brand} CMS Fee. Total Collection By Amik includes GST and covers all payment types.`,
      14,
      noteY + 5,
      { maxWidth: pageWidth - 28 }
    );

    // Transaction Details on a separate (landscape) page.
    if (data.transactions?.length) {
      doc.addPage("a4", "landscape");
      doc.setFontSize(13);
      doc.setTextColor(31, 56, 100);
      doc.text(`Transaction Details (${data.transactions.length})`, 12, 14);
      autoTable(doc, {
        startY: 20,
        head: [TXN_COLUMNS.map((c) => c.label)],
        body: data.transactions.map((t) =>
          TXN_COLUMNS.map((c) => {
            const v = t[c.key];
            if (c.time) return fmtTime(v);
            if (c.blankZero && !Number(v)) return "";
            if (c.money || c.num) return withComma(v);
            if (c.key === "paymentType" && t.ocpiCompany)
              return `${t.paymentType} · ${t.ocpiCompany}`;
            return v ?? "";
          })
        ),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [31, 56, 100] },
        columnStyles: {
          7: { halign: "right" },
          8: { halign: "right" },
          9: { halign: "right" },
          10: { halign: "right" },
          11: { halign: "right" },
          12: { halign: "right" },
          13: { halign: "right" },
          14: { halign: "right" },
        },
        theme: "grid",
        margin: { left: 8, right: 8 },
        didParseCell: (dd) => {
          if (dd.section === "body" && data.transactions[dd.row.index]?.overDelivered) {
            dd.cell.styles.fillColor = [255, 235, 200];
          }
        },
      });
    }

    doc.save(`Payout_${periodLabel.replace(" ", "_")}.pdf`);
  };

  // ---------------------------------------------------------------- render --
  return (
    <Page title="Payout">
      <Box sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Payout
        </Typography>

        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a year and month and the chargers to include (all by default),
            then generate the team payout. A session is counted in the month it
            completed.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {isGrandParent && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="payout-company">Company</InputLabel>
                    <Select
                      labelId="payout-company"
                      label="Company"
                      value={gpCompany}
                      onChange={(e) => {
                        setGpCompany(e.target.value);
                        setGpTeam("");
                        setData(null);
                      }}
                    >
                      {(companies || []).map((c) => (
                        <MenuItem key={c.teamId} value={c.teamId}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" disabled={!gpCompany}>
                    <InputLabel id="payout-team">Team</InputLabel>
                    <Select
                      labelId="payout-team"
                      label="Team"
                      value={gpTeam}
                      onChange={(e) => {
                        setGpTeam(e.target.value);
                        setData(null);
                      }}
                    >
                      {(gpTeams || []).map((t) => (
                        <MenuItem key={t.teamId} value={t.teamId}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="payout-year">Year</InputLabel>
                <Select
                  labelId="payout-year"
                  label="Year"
                  value={year}
                  onChange={(e) => {
                    setYear(e.target.value);
                    setMonth("");
                    setData(null);
                  }}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="payout-month">Month</InputLabel>
                <Select
                  labelId="payout-month"
                  label="Month"
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    setData(null);
                  }}
                >
                  {monthOptions.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="payout-chargers">Chargers</InputLabel>
                <Select
                  labelId="payout-chargers"
                  label="Chargers"
                  multiple
                  value={selectedChargers}
                  onChange={handleChargerChange}
                  renderValue={(sel) =>
                    allSelected
                      ? `All chargers (${sel.length})`
                      : `${sel.length} selected`
                  }
                >
                  {chargers.map((c) => (
                    <MenuItem key={c.chargepoint_pk} value={c.chargepoint_pk}>
                      <Checkbox
                        checked={selectedChargers.includes(c.chargepoint_pk)}
                      />
                      <ListItemText
                        primary={c.chargepoint_id}
                        secondary={c.chargepoint_name}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Full-width row: up to six actions appear here for a super admin,
                which cannot fit in a one-third column without the labels being
                squeezed. Wraps instead of shrinking on narrow screens. */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  flexWrap: "wrap",
                  gap: 1.5,
                  "& .MuiButton-root": { whiteSpace: "nowrap", flexShrink: 0 },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={
                    loading ||
                    chargersLoading ||
                    !month ||
                    (isGrandParent && !gpTeam)
                  }
                >
                  {loading ? <CircularProgress size={22} /> : "Generate Report"}
                </Button>
                {data && (
                  <>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadExcel}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleDownloadPDF}
                    >
                      PDF
                    </Button>
                    {isGrandParent && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={
                          finalizing ? (
                            <CircularProgress size={16} />
                          ) : (
                            <LockIcon />
                          )
                        }
                        onClick={handleFinalize}
                        disabled={finalizing}
                      >
                        {data.meta?.finalized ? "Re-finalize" : "Finalize month"}
                      </Button>
                    )}
                    {isGrandParent && (
                      <>
                        <Button
                          variant="outlined"
                          color="info"
                          startIcon={<EmailIcon />}
                          onClick={openEmailDialog}
                        >
                          Email
                        </Button>
                        <Button
                          variant="outlined"
                          color="info"
                          startIcon={<ScheduleIcon />}
                          onClick={openSchedules}
                        >
                          Schedules
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Card>

        {data && (
          <>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab value="summary" label="Summary" />
              <Tab
                value="transactions"
                label={`Transaction Details (${
                  data.transactions?.length || 0
                })`}
              />
            </Tabs>

            {tab === "summary" && (
              <>
            <Grid container spacing={3} sx={{ mb: 1 }}>
              <Grid item xs={12} md={5}>
                <Card sx={{ p: 2, height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Charging Summary
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {buildChargingSummary(data.summary, {
                          includePrice: false,
                          byPaymentType: data.byPaymentType,
                        }).map((m) => (
                          <TableRow key={m.label}>
                            <TableCell
                              sx={{
                                fontWeight: m.bold ? 700 : 400,
                                color: m.sub ? "text.secondary" : "inherit",
                              }}
                            >
                              {m.label}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: m.bold ? 700 : 400 }}
                            >
                              {m.int ? m.value : withComma(m.value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
              <Grid item xs={12} md={7}>
                <Card sx={{ p: 2, height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Payout Summary — {periodLabel}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Without GST</TableCell>
                          <TableCell align="right">With GST</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payoutRows(
                          data.summary,
                          data.payout,
                          data.meta,
                          data.transactions
                        ).map((pr) => (
                          <TableRow
                            key={pr.label}
                            sx={
                              pr.highlight
                                ? {
                                    bgcolor: "warning.lighter",
                                    "& td": { fontWeight: 700 },
                                  }
                                : undefined
                            }
                          >
                            <TableCell>{pr.label}</TableCell>
                            <TableCell align="right">
                              {withComma(pr.woGst)}
                            </TableCell>
                            <TableCell align="right">
                              {withComma(pr.wGst)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Payout = Total Collection By Amik − {brand} CMS Fee
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {buildPaymentTypeSections(
              data.byPaymentType,
              data.transactions
            ).map((s) =>
              renderPaymentTypeSection(
                s.title,
                s.rows,
                s.caption,
                s.emptyText,
                s.firstColLabel,
                s.key
              )
            )}

            <Card sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Per-Charger Payout ({data.chargers.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Charger</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Txns</TableCell>
                      <TableCell align="right">kWh</TableCell>
                      <TableCell align="right">Price/kWh (incl. GST)</TableCell>
                      <TableCell align="right">Charging Amount Collected</TableCell>
                      <TableCell align="right">{brand} CMS Fee</TableCell>
                      <TableCell align="right">Payout</TableCell>
                      {isGrandParent && data.meta?.finalized && (
                        <TableCell align="center">Edit</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.chargers.map((cg) => (
                      <TableRow
                        key={cg.chargerId}
                        sx={
                          cg.isOverridden
                            ? { bgcolor: "warning.lighter" }
                            : undefined
                        }
                      >
                        <TableCell>{cg.chargerId}</TableCell>
                        <TableCell>{cg.chargerType || "-"}</TableCell>
                        <TableCell align="right">
                          {cg.summary.totalTransactions}
                        </TableCell>
                        <TableCell align="right">
                          {withComma(cg.summary.totalKWh)}
                        </TableCell>
                        <TableCell align="right">
                          {withComma(cg.summary.avgPricePerKw)}
                        </TableCell>
                        <TableCell align="right">
                          {withComma(cg.summary.totalChargingCost)}
                        </TableCell>
                        <TableCell align="right">
                          {cg.isOverridden &&
                          Number(cg.originalAmikFee) !==
                            Number(cg.payout.amikFee) ? (
                            <>
                              <s style={{ color: "#999" }}>
                                {withComma(cg.originalAmikFee)}
                              </s>{" "}
                              <b>{withComma(cg.payout.amikFee)}</b>
                            </>
                          ) : (
                            withComma(cg.payout.amikFee)
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {cg.isOverridden &&
                          Number(cg.originalPayout) !==
                            Number(cg.payout.payout) ? (
                            <Tooltip
                              title={cg.overrideNote || "Overridden by super admin"}
                            >
                              <span>
                                <s style={{ color: "#999", fontWeight: 400 }}>
                                  {withComma(cg.originalPayout)}
                                </s>{" "}
                                {withComma(cg.payout.payout)}
                              </span>
                            </Tooltip>
                          ) : (
                            withComma(cg.payout.payout)
                          )}
                        </TableCell>
                        {isGrandParent && data.meta?.finalized && (
                          <TableCell align="center">
                            <Tooltip title="Override payout / fee">
                              <IconButton
                                size="small"
                                onClick={() => openEdit(cg)}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        {data.summary.totalTransactions}
                      </TableCell>
                      <TableCell align="right">
                        {withComma(data.summary.totalKWh)}
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        {withComma(data.summary.totalChargingCost)}
                      </TableCell>
                      <TableCell align="right">
                        {withComma(data.payout.amikFee)}
                      </TableCell>
                      <TableCell align="right">
                        {withComma(data.payout.payout)}
                      </TableCell>
                      {isGrandParent && data.meta?.finalized && <TableCell />}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" label={`Payout rule: ${formatPayoutRule(data.meta)}`} />
              <Chip size="small" label={`Period: ${periodLabel}`} />
              <Chip
                size="small"
                label={`Currency: ${data.meta?.currency || "INR"}`}
              />
              {data.meta?.finalized ? (
                <Chip
                  size="small"
                  color="success"
                  icon={<LockIcon />}
                  label={
                    data.meta?.finalizedAt
                      ? `Finalized ${fmtTime(data.meta.finalizedAt)}`
                      : "Finalized"
                  }
                />
              ) : (
                <Chip size="small" variant="outlined" label="Live (not finalized)" />
              )}
              {data.meta?.overridden && (
                <Chip size="small" color="warning" label="Contains overrides" />
              )}
            </Stack>
              </>
            )}

            {tab === "transactions" && (
              <Card sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Transaction Details ({data.transactions?.length || 0})
                </Typography>
                {(() => {
                  const od = (data.transactions || []).filter(
                    (t) => t.overDelivered
                  );
                  const total = od.reduce(
                    (a, t) => a + (t.overDeliveredAmount || 0),
                    0
                  );
                  return od.length ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {od.length} transaction(s) over-delivered (customer paid less
                      than charging cost, due to tolerance) — total{" "}
                      {withComma(total)} to adjust. Highlighted below with the
                      &quot;Over-delivery (Adj.)&quot; amount.
                    </Alert>
                  ) : null;
                })()}
                {(data.transactions?.length || 0) > TXN_SCREEN_LIMIT && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Showing first {TXN_SCREEN_LIMIT} of{" "}
                    {data.transactions.length}. Download Excel or PDF for the full
                    list.
                  </Typography>
                )}
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {TXN_COLUMNS.map((c) => (
                          <TableCell
                            key={c.key}
                            align={c.money || c.num ? "right" : "left"}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {c.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.transactions || [])
                        .slice(0, TXN_SCREEN_LIMIT)
                        .map((t) => (
                          <TableRow
                            key={`${t.chargerId}-${t.transactionId}`}
                            sx={
                              t.overDelivered
                                ? { bgcolor: "warning.lighter" }
                                : undefined
                            }
                          >
                            {TXN_COLUMNS.map((c) => (
                              <TableCell
                                key={c.key}
                                align={c.money || c.num ? "right" : "left"}
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                {c.time
                                  ? fmtTime(t[c.key])
                                  : c.blankZero && !Number(t[c.key])
                                  ? ""
                                  : c.money || c.num
                                  ? withComma(t[c.key])
                                  : c.key === "paymentType" && t.ocpiCompany
                                  ? `${t.paymentType} · ${t.ocpiCompany}`
                                  : t[c.key] ?? ""}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </>
        )}

        {/* Super-admin per-charger override dialog */}
        <Dialog
          open={!!editRow}
          onClose={() => setEditRow(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Override payout — {editRow?.chargerId}</DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary">
              Original payout: {withComma(editRow?.originalPayout)} · fee:{" "}
              {withComma(editRow?.originalAmikFee)}. Leave a field unchanged to keep
              the finalized value.
            </Typography>
            <TextField
              label={`Payout (${data?.meta?.currency || "INR"})`}
              type="number"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={editPayout}
              onChange={(e) => setEditPayout(e.target.value)}
            />
            <TextField
              label={`${brand} CMS Fee (${data?.meta?.currency || "INR"})`}
              type="number"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={editFee}
              onChange={(e) => setEditFee(e.target.value)}
            />
            <TextField
              label="Note (reason for override)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveOverride}
              disabled={savingOverride || !overrideDelta().changed}
            >
              {savingOverride ? <CircularProgress size={20} /> : "Save override"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email payout to customer */}
        <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Email payout — {periodLabel}</DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary">
              Sends the {allSelected ? "full team" : `${selectedChargers.length}-charger`} payout as
              PDF/Excel to the recipients. {data?.meta?.finalized ? "Finalized statement." : "Live (not finalized) figures."}
            </Typography>
            <TextField
              label="Recipients (comma-separated)"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              helperText={defaultRecipient ? `Team default: ${defaultRecipient}` : "Enter one or more emails"}
            />
            <TextField
              label="Subject (optional)"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            <TextField
              label="Message (optional)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <label>
                <Checkbox checked={emailPdf} onChange={(e) => setEmailPdf(e.target.checked)} size="small" />
                PDF
              </label>
              <label>
                <Checkbox checked={emailExcel} onChange={(e) => setEmailExcel(e.target.checked)} size="small" />
                Excel
              </label>
            </Stack>
            {emailResult && (
              <Alert severity={emailResult.ok ? "success" : "error"} sx={{ mt: 1 }}>
                {emailResult.msg}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={handleSendEmail}
              disabled={emailSending || (!emailPdf && !emailExcel)}
            >
              {emailSending ? <CircularProgress size={20} /> : "Send now"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage email schedules */}
        <Dialog open={schedOpen} onClose={() => setSchedOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Payout email schedules</DialogTitle>
          <DialogContent>
            {schedMsg && (
              <Alert severity={schedMsg.ok ? "success" : "error"} sx={{ mb: 1 }}>
                {schedMsg.msg}
              </Alert>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Existing schedules
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>When</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="caption" color="text.secondary">
                          No schedules yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {schedules.map((s) => (
                    <TableRow key={s.schedule_pk}>
                      <TableCell>{s.type}</TableCell>
                      <TableCell>
                        {s.type === "MONTHLY"
                          ? `Day ${s.day_of_month} (prev month)`
                          : `${fmtTime(s.send_at)} → ${s.target_month}/${s.target_year}`}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, wordBreak: "break-all" }}>
                        {s.recipients}
                      </TableCell>
                      <TableCell>
                        {[s.attach_pdf ? "PDF" : null, s.attach_excel ? "Excel" : null]
                          .filter(Boolean)
                          .join(" + ")}
                      </TableCell>
                      <TableCell>
                        {s.active ? (s.type === "ONCE" ? s.status : "Active") : "Paused"}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleToggleSchedule(s)}>
                          {s.active ? "Pause" : "Resume"}
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDeleteSchedule(s.schedule_pk)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add schedule
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={schedForm.type}
                    onChange={(e) => setSchedForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <MenuItem value="MONTHLY">Recurring monthly</MenuItem>
                    <MenuItem value="ONCE">One-off</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {schedForm.type === "MONTHLY" ? (
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Day of month (UTC)"
                    type="number"
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 28 }}
                    value={schedForm.dayOfMonth}
                    onChange={(e) => setSchedForm((f) => ({ ...f, dayOfMonth: e.target.value }))}
                    helperText="Emails the previous month"
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Send at (UTC)"
                      type="datetime-local"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={schedForm.sendAt}
                      onChange={(e) => setSchedForm((f) => ({ ...f, sendAt: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Year"
                      type="number"
                      fullWidth
                      size="small"
                      value={schedForm.targetYear}
                      onChange={(e) => setSchedForm((f) => ({ ...f, targetYear: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Month"
                      type="number"
                      fullWidth
                      size="small"
                      inputProps={{ min: 1, max: 12 }}
                      value={schedForm.targetMonth}
                      onChange={(e) => setSchedForm((f) => ({ ...f, targetMonth: e.target.value }))}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Recipients (comma-separated)"
                  fullWidth
                  size="small"
                  value={schedForm.recipients}
                  onChange={(e) => setSchedForm((f) => ({ ...f, recipients: e.target.value }))}
                  helperText={defaultRecipient ? `Team default: ${defaultRecipient}` : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <label>
                    <Checkbox
                      size="small"
                      checked={schedForm.attachPdf}
                      onChange={(e) => setSchedForm((f) => ({ ...f, attachPdf: e.target.checked }))}
                    />
                    PDF
                  </label>
                  <label>
                    <Checkbox
                      size="small"
                      checked={schedForm.attachExcel}
                      onChange={(e) => setSchedForm((f) => ({ ...f, attachExcel: e.target.checked }))}
                    />
                    Excel
                  </label>
                  <Button
                    variant="contained"
                    onClick={handleCreateSchedule}
                    disabled={schedBusy || !schedForm.recipients}
                  >
                    {schedBusy ? <CircularProgress size={20} /> : "Add schedule"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSchedOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
